import React, { Component } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import { isElementOf } from '../../util/react'
import VirtualList from '../virtual-list/virtual-list'
import virtualListPropType from '../virtual-list/virtual-list-prop-type'
import CompactListRow from './compact-list-row'

import styles from './compact-list.module.css'

const propTypes = {
    children: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    className: PropTypes.string,
    onSelect: PropTypes.func,
    overrideSelectionHighlighting: PropTypes.bool,
    resetSelection: PropTypes.bool,
    virtual: virtualListPropType
}

const defaultProps = {
    onSelect: () => {},
    overrideSelectionHighlighting: false,
    resetSelection: false
}

/**
 * @author Christian Seifert <christian.seifert@tomtom.com>
 *
 * The CompactList component to show a list of {@link CompactListRow} components, e.g. Vehicle list, notification
 * settings, reports or map-search results.
 *
 * {@link https://confluence.tomtomgroup.com/display/TTB/Molecules#Molecules-Rows | Row UX Design}
 *
 * @example
 * <CompactList onSelect={(rowId) => { console.log(`${rowId} is selected`) }}>
 *     <CompactListRow icon={<FoobarIcon />}>...</CompactListRow>
 * </CompactList>
 *
 * @param {[]}                     children                              - the children of this compact list
 * @param {string}                 [className]                           - a CSS class to append to the component
 * @param {Function}               [onSelect=()=>{}]                     - the children of this compact list
 * @param {boolean}                [overrideSelectionHighlighting=false] - override the selected row handling
 * @param {boolean}                [resetSelection=false]                - reset current selection?
 * @param {VirtualList}            [virtual]                             - virtualize the list for better performance
 */
class CompactList extends Component {
    constructor(props) {
        super(props)
        this.decorateChild = this.decorateChild.bind(this)

        this.state = {
            selectedRowId: null
        }
    }

    UNSAFE_componentWillReceiveProps(newProps) {
        // used to reset selectedRowId when e.g. item detail view is closed
        if (newProps.resetSelection && !this.props.resetSelection) {
            this.state = {
                selectedRowId: null
            }
        }
    }

    render() {
        const { children, className, virtual } = this.props

        return (
            <div className={cx(styles['compact-list'], className)}>
                {
                    // if a compact list row is expandable, the virtual list does not work as expected
                    // @link https://jira.ttt-sp.com/browse/ATL-5200
                    virtual ? (
                        <VirtualList settings={virtual} childDecorator={this.decorateChild}>
                            {children}
                        </VirtualList>
                    ) : (
                        React.Children.map(children, this.decorateChild)
                    )
                }
            </div>
        )
    }

    decorateChild(child) {
        const { overrideSelectionHighlighting, virtual } = this.props
        if (!isElementOf(child, CompactListRow)) {
            return child
        }

        const childProps = { isListVirtual: !!virtual }
        if (!overrideSelectionHighlighting) {
            childProps.isSelected = this.state.selectedRowId === child.props.id
        }

        return <div onClick={this.createSelectFn(child.props.id)}>{React.cloneElement(child, childProps)}</div>
    }

    createSelectFn(id) {
        const { overrideSelectionHighlighting } = this.props

        return () => {
            this.props.onSelect(id)
            if (!overrideSelectionHighlighting) {
                this.setState({
                    selectedRowId: id
                })
            }
        }
    }
}

CompactList.propTypes = propTypes
CompactList.defaultProps = defaultProps

export default CompactList
